import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';

import { serialNumbersService } from 'services';
import { ROUTES } from 'constants/routes';
import { CustomInput, CustomButton, Spinner } from 'components';

import './EditOriginalPdfForm.scss';
import { ORIGINAL_PDF_LIST } from '../../constants/originalPdfList';
import { SerialNumber } from '../../interfaces/serialNumber';
import axios from 'axios';
import { useAppSelector } from '../../hooks';
import {
  originalValidationSchemeAgent,
  originalValidationSchemeCustomer,
} from '../../validation';
import { AnyObject, TestContext } from 'yup';

export const EditOriginalPdfForm = () => {
  const { sn, commissioningToken } = useParams();
  const navigate = useNavigate();
  const [errMessage, setErrMessage] = useState<string>('');

  const [isUpdateRequestLoading, setIsUpdateRequestLoading] =
    useState<boolean>(false);
  const [isSuccessfullyUpdated, setIsSuccessfullyUpdated] =
    useState<boolean>(false);

  const { user } = useAppSelector((state) => state.auth);

  const [initialValues, setInitialValues] = useState<SerialNumber>({
    sn: 0,
    imo_nr: '',
    schiffsname: '',
    produktionsdatum: '',
    sn_partner: '',
    commissioning_date: '',
    email: '',
  } as SerialNumber);

  const getFilteredFields = () => {
    return ORIGINAL_PDF_LIST.filter((field) => {
      if (field.id === 'commissioning_date') {
        field.disabled =
          !commissioningToken || !!initialValues.commissioning_date;
        return true;
      }

      if (!user && initialValues?.sn) return true;

      return field.id !== 'email' && field.id !== 'produktionsdatum';
    });
  };

  const [fields, setFields] = useState(getFilteredFields());

  const computerSnField = fields.find((o) => o.id === 'sn_partner');

  const snValidation = async (value: any, ctx: TestContext<AnyObject>) => {
    const sn = value.toString();

    const serial = await serialNumbersService.findSerial(sn);

    if (computerSnField) computerSnField.disabled = serial.typ !== 'Messzelle';

    return true;
  };

  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      setIsUpdateRequestLoading(true);
      setErrMessage('');

      try {
        values.commissioning_token = commissioningToken || '';
        await serialNumbersService.update(values);

        setIsUpdateRequestLoading(false);
        setIsSuccessfullyUpdated(true);

        navigate(`${ROUTES.documents_overview}/${values.sn}`);
      } catch (e) {
        setIsUpdateRequestLoading(false);
        setIsSuccessfullyUpdated(false);
        if (axios.isAxiosError(e) && e.response)
          setErrMessage(e.response.data?.message || 'Something went wrong');
      }
    },
    enableReinitialize: true,
    validationSchema: user
      ? originalValidationSchemeAgent(snValidation)
      : originalValidationSchemeCustomer(
          snValidation,
          !computerSnField?.disabled || false
        ),
  });

  useEffect(() => {
    setFields(getFilteredFields());
  }, [user, commissioningToken, initialValues]);

  useEffect(() => {
    setIsSuccessfullyUpdated(false);

    (async () => {
      if (!sn) {
        return;
      }

      const response = await serialNumbersService
        .findSerial(sn)
        .catch(() => navigate(ROUTES.pdf_history));

      if (!response) {
        return;
      }

      setInitialValues({
        sn: response.sn,
        imo_nr: response.imo_nr ? Number(response.imo_nr) : '',
        schiffsname: response.schiffsname,
        produktionsdatum: '',
        email: '',
        sn_partner: response.sn_partner || '',
        commissioning_date: response.commissioning_date?.slice(0, 10) || '',
      } as SerialNumber);

      if (computerSnField) {
        computerSnField.disabled = response.typ !== 'Messzelle';
      }
    })();
  }, [sn]);

  return (
    <div className="edit-pdf-container">
      <h1 className="edit-pdf__header">Edit calibration</h1>
      <form onSubmit={formik.handleSubmit}>
        <div className="edit-pdf-form-list">
          {fields.map(({ id, name, type, disabled }) => {
            const identifier = id as keyof SerialNumber;

            if (type === 'gab') return <div></div>;
            if (type === 'space')
              return <div key={id} className="generate-pdf__space"></div>;

            return (
              <div key={id}>
                <CustomInput
                  id={id}
                  name={id}
                  disabled={disabled}
                  value={formik.values[identifier]}
                  onChange={formik.handleChange}
                  label={name}
                  type={type}
                />
                {formik.touched[identifier] && formik.errors[identifier] && (
                  <div className="edit-pdf__error-validation-box">
                    <p className="edit-pdf__error-validation-text">
                      {formik.errors[identifier]}
                    </p>
                  </div>
                )}
                {identifier === 'produktionsdatum' && (
                  <div className="generate-pdf__info-box">
                    <p className="generate-pdf__info-text">
                      The productions date is to find on the type plate of the
                      Measuring Cell and written in the Format (mm/yy).
                    </p>
                  </div>
                )}
                {identifier === 'commissioning_date' && (
                  <div className="generate-pdf__info-box">
                    <p className="generate-pdf__info-text">
                      The commissioning date is pre-set to the calibration date,
                      as we do not have any references or documents that prove a
                      different commissioning date. If you would like to have
                      your commissioning date changed please contact:
                      <a href="mailto:post@deckma.com">post@deckma.com</a>
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!isUpdateRequestLoading && (
          <div className="edit-pdf__button-box">
            <CustomButton type="submit" text="Update calibration" width="35%" />
          </div>
        )}
        {isUpdateRequestLoading && (
          <div className="edit-pdf__spinner-box">
            <Spinner />
          </div>
        )}
        {isSuccessfullyUpdated && (
          <div className="edit-pdf__success">Successfully updated</div>
        )}
        {errMessage && <div className="edit-pdf__error">{errMessage}</div>}
      </form>
    </div>
  );
};
